/**
 * Ficha única de palabra (20.H, H2) — UNA sola ficha para todo el producto:
 * se abre igual desde Explorar, Lo mío, el buscador o los resultados de juego.
 * Port del bundle (biblioteca-ficha.jsx) con audio REAL (el de la palabra vía
 * `speakText`, o el de su pronunciación en un heterónimo; anti-teatro H8) y CTA
 * de práctica honesto ("Agregar al deck").
 *
 * Se dibuja como la D de la ficha rediseñada (LA-FICHA-REDISENADA.md, elegida
 * por Emmanuel el 05/10, decisión 80): arriba la palabra, cómo suena y qué tan
 * común es; con el diccionario nuevo (v2) y varios significados, una etiqueta
 * por significado que se toca (la D de la decisión 70, con la C de la 78); y
 * debajo, en un recuadro, todo lo del significado elegido: su tipo y su nivel,
 * la traducción, «También», la definición y el ejemplo. Con un solo
 * significado, o sin significados (v1, copias de Lo mío), el recuadro es el de
 * la palabra. El estado lo dicen los botones, en una fila fija al pie.
 */
import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useAudioBlob } from '../../lib/audioBlobPlayer.js';
import { describeSpeakError } from '../../lib/englishApi.js';
import { speakPronunciation } from '../../lib/senseAudio.js';
import type { PhrasalMeta, VerbForms } from '../../lib/lexiconApi.js';
import { VOCAB_TOPIC_IDS, type VocabTopic } from '@orborix/shared-types';
import type { VocabEntryPatch } from '../../lib/vocabApi.js';
import { MeaningReport } from './MeaningReport.js';
import { fetchDictionaryEntry } from '../../lib/lexiconApi.js';
import type { LexiconReportContext } from '@orborix/shared-types';
import { VOCAB_TOPIC_META, type CefrLevel, type VocabTag } from '../vocabulary/vocabData.js';
import { TagBadge, TagPicker } from '../vocabulary/VocabBits.js';
import {
  LIB_POS_VERB,
  libFromLexicon,
  libOverCollection,
  libAlso,
  libBandOf,
  libBandText,
  libCefrHint,
  libChipParts,
  libChipsFit,
  libExampleMark,
  libMeaning,
  libPosInfo,
  libPosName,
  type LibMeaning,
  type LibWord,
} from './libData.js';
import { LIB_FAM, LibSheet } from './libShared.js';
import {
  LibIcBookmark,
  LibIcCards,
  LibIcCheck,
  LibIcCheckC,
  LibIcChevR,
  LibIcSpeaker,
  LibIcStar,
  LibIcWeak,
} from './libIcons.js';
import { TapText, useSheetTap, type SheetTapLookup } from './SheetTap.js';

// Botón de audio real (nunca barras fake): el audio de la palabra, o, en un
// heterónimo, el de la pronunciación que se ve escrita al lado (`senseAudio.ts`).
// `entry`: es una entrada del diccionario, y un verbo frasal (`give up`) suena
// con el audio de las palabras aunque tenga espacio.
function LibAudioIPA({ word, ipa, entry }: { word: string; ipa: string; entry: boolean }) {
  const tts = useAudioBlob();
  const playing = tts.state === 'playing';
  // La primera vez que alguien toca una palabra se genera y se controla, y
  // puede tardar unos segundos: sin esto el botón parecía roto (misión 66).
  const loading = tts.state === 'loading';
  // Es un ícono sin lugar para un cartel: el motivo del fallo (típicamente el
  // tope de audios por minuto) va al tooltip + label, que es lo que hay.
  const failed = tts.state === 'error' ? describeSpeakError(tts.error) : null;
  return (
    <button
      className={`bf-ipa ${playing ? 'on' : ''} ${loading ? 'loading' : ''}`}
      onClick={() => void tts.toggle(() => speakPronunciation(word, ipa, { entry }))}
      title={failed ?? undefined}
      aria-label={failed ?? (loading ? 'Cargando audio' : 'Escuchar pronunciación')}
      aria-busy={loading}
    >
      {playing ? (
        <span className="bf-eq" aria-hidden="true">
          <i></i>
          <i></i>
          <i></i>
          <i></i>
        </span>
      ) : loading ? (
        <span className="bf-spin" aria-hidden="true" />
      ) : (
        <LibIcSpeaker size={14} />
      )}
      <span>{ipa}</span>
    </button>
  );
}

/** La conjugación, con sus nombres en palabras («he, she», no «3.ª pers.»). */
function LibConjBlock({ conj }: { conj: VerbForms }) {
  const cell = (label: string, value: string, irr = false) => (
    <div className={`bf-conj-cell ${irr ? 'irr' : ''}`}>
      <small>{label}</small>
      <b>{value}</b>
    </div>
  );
  return (
    <div className="bf-block">
      <div className="bf-block-title">
        Conjugación{conj.irregular && <span className="bf-irr">irregular</span>}
      </div>
      <div className="bf-conj">
        {cell('base', conj.base)}
        {cell('he, she', conj.thirdPerson)}
        {cell('-ing', conj.gerund)}
      </div>
      <div className="bf-conj bf-conj-row2">
        {cell('pasado', conj.past, conj.irregular)}
        {cell('participio', conj.pastParticiple, conj.irregular)}
      </div>
    </div>
  );
}

/**
 * El verbo frasal en una línea y una frase (`frasal` de la maqueta). Uno
 * separable lleva objeto: dice dónde va. No dice «sin objeto» de uno
 * separable, que es un dato contradictorio del catálogo (LA-FICHA-REDISENADA.md).
 */
function LibPhrasalBlock({ phrasal }: { phrasal: PhrasalMeta }) {
  const base = <b>{phrasal.base}</b>;
  const particle = <b className="particle">{phrasal.particle}</b>;
  const obj = <i>algo</i>;
  const [forms, note] = phrasal.separable
    ? [
        <>
          {base} {particle} {obj} · {base} {obj} {particle}
        </>,
        'Se puede separar: «algo» va al final o en el medio.',
      ]
    : phrasal.transitive
      ? [
          <>
            {base} {particle} {obj}
          </>,
          'No se separa: «algo» va siempre al final.',
        ]
      : [
          <>
            {base} {particle}
          </>,
          'No lleva objeto.',
        ];
  return (
    <div className="bf-block">
      <div className="bf-block-title">Phrasal verb</div>
      <p className="bf-phrasal">{forms}</p>
      <p className="bf-phrasal-note">{note}</p>
    </div>
  );
}

/** El ejemplo, en la letra de la ficha y sin comillas, con la palabra (o una forma suya) resaltada si aparece. */
function LibExample({
  w,
  example,
  tap,
}: {
  w: LibWord;
  example: string;
  tap: SheetTapProps | null;
}) {
  const hit = libExampleMark(w, example);
  if (tap) {
    return (
      <p className="bf-ex">
        <TapText
          text={example}
          origin="example"
          lang="en"
          mark={hit}
          active={tap.active}
          onTap={tap.onTap}
        />
      </p>
    );
  }
  return (
    <p className="bf-ex">
      {hit ? (
        <>
          {example.slice(0, hit.start)}
          <mark>{example.slice(hit.start, hit.end)}</mark>
          {example.slice(hit.end)}
        </>
      ) : (
        example
      )}
    </p>
  );
}

/**
 * Cuántas etiquetas entran en dos filas, midiendo cada una en el navegador
 * (`acomodar` de la maqueta). Se miden todas a la vista y después se les
 * devuelve el `hidden` que tenían, porque ese atributo lo maneja React. Null
 * sin layout (jsdom): ahí se ven todas y no hay «otros».
 */
function measureChips(box: HTMLElement): number | null {
  const width = box.clientWidth;
  if (!width) return null;
  const chips = [...box.querySelectorAll<HTMLElement>('.d-chip')];
  const other = box.querySelector<HTMLElement>('.d-otros');
  if (!other) throw new Error('measureChips: la caja de etiquetas no tiene «otros»');
  const all = [...chips, other];
  const wasHidden = all.map((el) => el.hidden);
  for (const el of all) el.hidden = false;
  const widthOf = (el: HTMLElement) => el.getBoundingClientRect().width;
  const fit = libChipsFit(chips.map(widthOf), width, { otherWidth: widthOf(other) });
  all.forEach((el, i) => {
    el.hidden = wasHidden[i]!;
  });
  return fit;
}

/**
 * Una traducción en la forma C (ETIQUETAS-EN-LA-FICHA.md, decisión 78): la
 * traducción, y lo que iba entre paréntesis como aclaración, sin ellos, más
 * chica y en gris (`asideClass`). La usan las etiquetas (`.d-acl`) y la
 * traducción grande (`.bf-tr-acl`). Los pedazos van separados por un espacio,
 * como en la maqueta; `measureChips` mide lo que se dibuja, así que la regla de
 * las dos filas no cambia.
 */
function LibChipText({ label, asideClass = 'd-acl' }: { label: string; asideClass?: string }) {
  return libChipParts(label).map((p, i) => (
    <Fragment key={i}>
      {i > 0 && ' '}
      {p.aside ? <span className={asideClass}>{p.text}</span> : p.text}
    </Fragment>
  ));
}

/**
 * Las etiquetas de la D: una por significado, en el orden del diccionario, la
 * elegida resaltada. Dos filas como máximo; las que no entran van detrás de
 * «otros», que las abre debajo con «menos» para volver. Se vuelve a medir si
 * cambia el ancho de la ficha o terminan de cargar las fuentes.
 */
function LibMeaningChips({
  meanings,
  selectedId,
  onSelect,
}: {
  meanings: readonly LibMeaning[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => setFit(measureChips(box));
    measure();
    if ('fonts' in document) void document.fonts.ready.then(measure);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [meanings]);

  const folded = !open && fit != null && fit < meanings.length;
  return (
    <div className="bf-acc d-chips" ref={boxRef}>
      {meanings.map((m, i) => (
        <button
          key={m.id}
          type="button"
          className={`bf-acc-item d-chip ${m.id === selectedId ? 'on' : ''}`}
          aria-pressed={m.id === selectedId}
          title={m.tr}
          hidden={folded && fit != null && i >= fit}
          onClick={() => onSelect(m.id)}
        >
          <LibChipText label={m.tr} />
        </button>
      ))}
      <button
        type="button"
        className="bf-acc-item d-otros"
        hidden={!folded}
        onClick={() => setOpen(true)}
      >
        otros
      </button>
      <button
        type="button"
        className="bf-acc-item d-menos"
        hidden={!open}
        onClick={() => setOpen(false)}
      >
        menos
      </button>
    </div>
  );
}

/**
 * Lo que muestra el recuadro: el significado elegido (v2) o, sin significados
 * (v1, copias de Lo mío), la palabra.
 */
interface LibShown {
  pos: string | null;
  cefr: CefrLevel | null;
  tr: string;
  also: string[];
  definition: string | null;
  example: string | null;
  exampleEs: string | null;
}

function shownOfMeaning(m: LibMeaning): LibShown {
  return {
    pos: m.pos,
    cefr: m.cefr,
    tr: m.tr,
    also: libAlso(m.tr, m.alts),
    definition: m.definition,
    example: m.example,
    exampleEs: m.exampleEs,
  };
}

function shownOfWord(w: LibWord): LibShown {
  return {
    pos: w.pos,
    cefr: w.cefr,
    tr: w.tr || '—',
    also: libAlso(w.tr, w.senses),
    definition: w.definition,
    example: w.example,
    exampleEs: w.exampleEs,
  };
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * El tipo y el nivel de lo que muestra el recuadro, escritos enteros
 * («determinante · A2»); al tocarlos se explican con palabras. Una etiqueta que
 * no es del catálogo (la de una copia de Lo mío) se ve como está y, sin nivel,
 * no tiene nada que explicar.
 */
function LibTypeLine({
  pos,
  cefr,
  open,
  onToggle,
}: {
  pos: string | null;
  cefr: CefrLevel | null;
  open: boolean;
  onToggle: () => void;
}) {
  const label = [pos ? libPosName(pos) : null, cefr].filter(Boolean).join(' · ');
  if (!label) return null;
  const info = pos ? libPosInfo(pos) : null;
  if (!info && !cefr) return <span className="bf-type plain">{label}</span>;
  return (
    <>
      <button type="button" className="bf-type" aria-expanded={open} onClick={onToggle}>
        {label}
      </button>
      {open && (
        <p className="bf-expl" role="note">
          {info && (
            <>
              <b>{capitalize(info.name)}</b>: {info.what}
              {info.examples.length > 0 ? ` (${info.examples.join(', ')})` : ''}.
            </>
          )}
          {info && cefr && ' '}
          {cefr && (
            <>
              <b>{cefr}</b>: nivel {libCefrHint(cefr)}.
            </>
          )}
        </p>
      )}
    </>
  );
}

/**
 * El recuadro de la D: todo lo del significado elegido junto, debajo de las
 * etiquetas (su tipo y su nivel, la traducción grande en la forma C,
 * «También» sin lo que ya se ve, la definición, el ejemplo y su traducción).
 * Tocar una etiqueta cambia sólo esto (y, en un heterónimo, la pronunciación
 * del encabezado); lo que se guarda y lo que usan los juegos sigue siendo el
 * principal (`w.tr`, `w.example`).
 */
/** Textos tocables del recuadro (encargo 49). */
type SheetTapProps = Pick<ReturnType<typeof useSheetTap>, 'active' | 'onTap'>;
function LibMeaningPanel({
  w,
  shown,
  typeOpen,
  onToggleType,
  hidden = false,
  report,
  tap,
}: {
  w: LibWord;
  shown: LibShown;
  typeOpen: boolean;
  onToggleType: () => void;
  hidden?: boolean;
  report?: React.ReactNode;
  tap: SheetTapProps | null;
}) {
  return (
    <div className="bf-panel">
      {hidden ? (
        <p role="status">Este significado está oculto para vos.</p>
      ) : (
        <>
          <LibTypeLine pos={shown.pos} cefr={shown.cefr} open={typeOpen} onToggle={onToggleType} />
          <div className="bf-tr">
            <span className="bf-tr-txt">
              <LibChipText label={shown.tr} asideClass="bf-tr-acl" />
            </span>
          </div>
          {shown.also.length > 0 && (
            <p className="d-alt">
              <b>También:</b> {shown.also.join(', ')}
            </p>
          )}
          {shown.definition && (
            <p className="bf-def">
              {tap ? (
                <TapText
                  text={shown.definition}
                  origin="definition"
                  lang="en"
                  active={tap.active}
                  onTap={tap.onTap}
                />
              ) : (
                shown.definition
              )}
            </p>
          )}
          {shown.example && <LibExample w={w} example={shown.example} tap={tap} />}
          {shown.exampleEs && (
            <p className="ex-es">
              {tap ? (
                <TapText
                  text={shown.exampleEs}
                  origin="example-es"
                  lang="es"
                  active={tap.active}
                  onTap={tap.onTap}
                />
              ) : (
                shown.exampleEs
              )}
            </p>
          )}
        </>
      )}
      {report}
    </div>
  );
}

/**
 * La palabra tal como está en la colección del usuario, con lo que se edita
 * desde la ficha: etiquetas, favorita, "me cuesta" y el tema que puso el
 * coach. La ficha única conserva la edición del WordSheet viejo (20.H, H2) y
 * es el único lugar donde se edita UNA palabra: la pantalla Vocabulario se
 * absorbió en Lo mío (card #348). Los estilos de `TagBadge`/`TagPicker` vienen
 * de `vocabulary.css`, que carga el shell de la Biblioteca.
 */
export interface LibWordCollection {
  /** La entrada VIVA (no la foto con la que se abrió la ficha). */
  entry: {
    tags: readonly string[];
    favorite: boolean;
    difficulty: boolean;
    topic: VocabTopic | null;
  };
  /** Todas las etiquetas del usuario. */
  allTags: readonly VocabTag[];
  onChange: (patch: VocabEntryPatch) => void;
  onCreateTag: (name: string) => Promise<string>;
}

function LibTagsRow({ collection }: { collection: LibWordCollection }) {
  const { entry, allTags, onChange, onCreateTag } = collection;
  const value = entry.tags;
  const [picking, setPicking] = useState(false);
  const mine = value
    .map((id) => allTags.find((t) => t.id === id))
    .filter((t): t is VocabTag => t != null);
  return (
    <>
      <div className="bf-row">
        <span className="bf-row-k">Etiquetas</span>
        <span className="bf-row-v">
          {mine.length > 0 ? (
            <span className="bf-labels">
              {mine.map((t) => (
                <TagBadge
                  key={t.id}
                  tag={t}
                  onRemove={(id) => onChange({ tags: value.filter((x) => x !== id) })}
                />
              ))}
            </span>
          ) : (
            'Ninguna'
          )}
        </span>
        <button className="bf-block-btn" onClick={() => setPicking((p) => !p)}>
          {picking ? 'Listo' : mine.length ? 'Editar' : 'Etiquetar'}
        </button>
      </div>
      {picking && (
        <div className="bf-labels-pick">
          <TagPicker
            tags={allTags}
            value={[...value]}
            onToggle={(id) =>
              onChange({
                tags: value.includes(id) ? value.filter((x) => x !== id) : [...value, id],
              })
            }
            onCreate={(name) => {
              void onCreateTag(name).then((id) => onChange({ tags: [...value, id] }));
            }}
          />
        </div>
      )}
    </>
  );
}

// El tema lo pone el coach solo; acá se corrige si le erró.
function LibTopicRow({ collection }: { collection: LibWordCollection }) {
  const { entry, onChange } = collection;
  const [picking, setPicking] = useState(false);
  const pick = (topic: VocabTopic | null) => {
    onChange({ topic });
    setPicking(false);
  };
  return (
    <>
      <div className="bf-row">
        <span className="bf-row-k">Tema</span>
        <span className="bf-row-v">
          {entry.topic ? (
            <>
              <i
                className="bf-topic-dot"
                style={{ background: VOCAB_TOPIC_META[entry.topic].color }}
              />
              {VOCAB_TOPIC_META[entry.topic].label}
            </>
          ) : (
            'Sin tema'
          )}
        </span>
        <button className="bf-block-btn" onClick={() => setPicking((p) => !p)}>
          {picking ? 'Listo' : 'Cambiar'}
        </button>
      </div>
      {picking && (
        <div className="bf-topics">
          {VOCAB_TOPIC_IDS.map((t) => (
            <button
              key={t}
              className={`bf-topic-opt ${entry.topic === t ? 'on' : ''}`}
              aria-pressed={entry.topic === t}
              onClick={() => pick(t)}
            >
              <span className="bf-topic-dot" style={{ background: VOCAB_TOPIC_META[t].color }} />
              {VOCAB_TOPIC_META[t].label}
            </button>
          ))}
          <button
            className={`bf-topic-opt ${entry.topic === null ? 'on' : ''}`}
            aria-pressed={entry.topic === null}
            onClick={() => pick(null)}
          >
            Sin tema
          </button>
        </div>
      )}
    </>
  );
}

/** De dónde salió una palabra de Lo mío, si no la guardó el alumno desde el diccionario. */
function mineOrigin(w: LibWord): string | null {
  if (w.isPhrase)
    return w.fromSessions
      ? 'Una frase que detectó el coach en una charla.'
      : 'Una frase que guardaste.';
  return w.fromSessions ? 'La detectó el coach en una charla.' : null;
}

/**
 * Lo de Lo mío en un solo bloque: cómo la viene practicando, de dónde salió,
 * favorita y «me cuesta», etiquetas y tema. Sin la entrada de la colección
 * (los resultados de un juego) no se edita nada: se ve lo que trae la palabra.
 */
function LibMineBlock({ w, collection }: { w: LibWord; collection: LibWordCollection | null }) {
  const famT = w.fam && !w.dom ? LIB_FAM[w.fam] : null;
  const origin = mineOrigin(w);
  return (
    <div className="bf-block bf-mine">
      <div className="bf-mine-head">
        <span className="bf-block-title">En Lo mío</span>
        {famT && (
          <span className="bf-fam" style={{ color: famT.color }}>
            <i style={{ background: famT.color }} />
            {famT.label}
          </span>
        )}
      </div>
      {origin && <p className="bf-mine-origin">{origin}</p>}
      {collection ? (
        <>
          {/* Favorita y "me cuesta" se prenden y apagan acá (con borde punteado cuando están apagados). */}
          <div className="bf-states bf-tgls">
            <button
              className={`bf-state bf-tgl ${collection.entry.favorite ? 'on fav' : ''}`}
              aria-pressed={collection.entry.favorite}
              onClick={() => collection.onChange({ favorite: !collection.entry.favorite })}
            >
              <LibIcStar size={12} fill={collection.entry.favorite ? 'currentColor' : 'none'} />{' '}
              Favorita
            </button>
            <button
              className={`bf-state bf-tgl ${collection.entry.difficulty ? 'on hard' : ''}`}
              aria-pressed={collection.entry.difficulty}
              onClick={() => collection.onChange({ difficulty: !collection.entry.difficulty })}
            >
              <LibIcWeak size={12} /> Me cuesta
            </button>
          </div>
          <LibTagsRow collection={collection} />
          <LibTopicRow collection={collection} />
        </>
      ) : (
        (w.fav || w.hard) && (
          <div className="bf-states bf-tgls">
            {w.fav && (
              <span className="bf-state on fav">
                <LibIcStar size={12} fill="currentColor" /> Favorita
              </span>
            )}
            {w.hard && (
              <span className="bf-state on hard">
                <LibIcWeak size={12} /> Me cuesta
              </span>
            )}
          </div>
        )
      )}
    </div>
  );
}

/** El círculo lleno con la tilde a la vista: «Ya la sé» marcada, o dicha. */
const LibIcKnown = () => <LibIcCheckC size={18} fill="currentColor" tick="var(--lime)" />;

export function WordSheetLib({
  w: initialWord,
  inDeck = false,
  onClose,
  onToggleSave,
  onAddToDeck,
  onToggleMastered,
  usage,
  collection = null,
  initialMeaningId,
  dictionaryFailed = false,
  contextNote = null,
  back,
  tapLookup,
  presentation = 'sheet',
}: {
  w: LibWord;
  /** Ya está en el deck local de práctica de esta sesión. */
  inDeck?: boolean;
  onClose: () => void;
  onToggleSave: (w: LibWord) => void;
  /** Sin handler, el CTA de deck no se muestra (contextos sin práctica). */
  onAddToDeck?: (w: LibWord) => void;
  /** Marcar/des-marcar "ya la sé" (mueve la cobertura). Sin handler no se
   *  toca: si ya la sabe, el botón queda fijo, lleno (contextos sin catálogo). */
  onToggleMastered?: (w: LibWord) => void;
  /** Bloque contextual opcional (ej. listen: "En este capítulo" + la oración). */
  usage?: { label: string; node: React.ReactNode };
  /** Sólo con la palabra en la colección; sin esto (catálogo sin guardar,
   *  listen) no se edita nada: ni etiquetas, ni favorita, ni tema. */
  collection?: LibWordCollection | null;
  /** En qué significado abre la D (el que encontró el buscador). Sin esto, el
   *  principal. Tiene que ser uno de `w.meanings`. */
  initialMeaningId?: string;
  /** Lo mío no pudo leer la ficha del diccionario: se ve la copia guardada, y
   *  la ficha lo dice (no es lo mismo que una palabra que salió). */
  dictionaryFailed?: boolean;
  /** «No pudimos elegir el significado…» (encargo 49): arriba de los significados. */
  contextNote?: string | null;
  /** Una ficha abierta desde otra (encargo 49): «Volver» a la anterior. */
  back?: { label: string; onBack: () => void };
  /** Con esto, la definición, el ejemplo y su traducción se pueden tocar (encargo 49). */
  tapLookup?: SheetTapLookup;
  /** `panel`: fija al lado de la lista (la Biblioteca en tablet acostada y
   *  compu, card #592), sin velo ni cruz. Por defecto, la hoja que sube. */
  presentation?: 'sheet' | 'panel';
}) {
  const [reportState, setReportState] = useState<{
    of: string;
    value: LexiconReportContext;
  } | null>(null);
  const [restored, setRestored] = useState<LibWord | null>(null);
  const w = restored?.id === initialWord.id ? restored : initialWord;
  const reportContext =
    reportState?.of === initialWord.id ? reportState.value : initialWord.reportContext;
  const onReportContext = useCallback(
    (value: LexiconReportContext) => setReportState({ of: initialWord.id, value }),
    [initialWord.id],
  );
  const [added, setAdded] = useState(inDeck);
  const band = w.rank < 99999 ? libBandOf(w.rank) : null;
  // La D: más de un significado (v2), con sus etiquetas. Con uno solo, el
  // recuadro es el suyo; sin significados (v1, copias), el de la palabra.
  const meanings = w.meanings && w.meanings.length > 1 ? w.meanings : null;
  const principalId = meanings?.[0]?.id ?? null;
  const single = w.meanings?.length === 1 ? w.meanings[0] : undefined;
  // El significado elegido de la D: abrir otra palabra (u otro significado
  // inicial) vuelve al inicial, y cierra las explicaciones. Vive acá porque la
  // pronunciación del encabezado es la del elegido si tiene una propia (un
  // heterónimo, decisión 79: `live` «en vivo» /laɪv/); si no, la de la
  // palabra. El parlante dice la pronunciación que se ve (decisión 81).
  const startId = initialMeaningId ?? principalId;
  const openKey = `${w.id}|${startId ?? ''}`;
  const [pick, setPick] = useState<{ of: string; id: string } | null>(null);
  const selectedId = pick?.of === openKey ? pick.id : startId;
  const selected = meanings && selectedId ? libMeaning(w, selectedId) : null;
  const currentMeaningId = selected?.id ?? single?.id ?? null;
  const personallyHidden = currentMeaningId
    ? !!reportContext?.hiddenIds.includes(currentMeaningId)
    : !!reportContext?.hiddenIds.length;
  // Sin contrato de significado (copia tras retiro), sólo historia/protección; nunca un nuevo envío.
  const reportMeaningId =
    currentMeaningId ??
    reportContext?.hiddenIds[0] ??
    reportContext?.reports.find((r) => r.meaningId)?.meaningId ??
    (reportContext?.reports.length ? 'missing' : null);
  const visibleMeanings = meanings?.map((m) =>
    reportContext?.hiddenIds.includes(m.id) ? { ...m, tr: 'Oculto para vos' } : m,
  );
  const ipa = selected?.ipa ?? w.ipa;
  const shown = selected
    ? shownOfMeaning(selected)
    : single
      ? shownOfMeaning(single)
      : shownOfWord(w);
  // Qué explicación está abierta: la del tipo (del significado que se ve) o
  // la de la frecuencia. Una a la vez; tocar otra etiqueta cierra la del tipo.
  const [expl, setExpl] = useState<{ of: string; what: 'type' | 'band' } | null>(null);
  const openExpl = expl?.of === openKey ? expl.what : null;
  const toggleExpl = (what: 'type' | 'band') =>
    setExpl(openExpl === what ? null : { of: openKey, what });
  // Tocar una palabra adentro: el resultado se limpia al cambiar de ficha o de significado.
  const tap = useSheetTap(
    tapLookup,
    `${openKey}|${selectedId ?? ''}`,
    selectedId ?? single?.id ?? w.text,
  );
  const tapProps = tapLookup ? { active: tap.active, onTap: tap.onTap } : null;

  // El estado lo dicen los botones: «Ya la sé» lleno si la marcó; «La dijiste»
  // fijo si la produjo (no se des-marca lo que dijo). Sin handler, sólo se ve
  // si ya la sabe.
  const said = w.dom && w.said;
  const know = w.isPhrase ? null : said ? (
    <div className="bf-act know on fixed" aria-disabled="true">
      <LibIcKnown />
      <span>La dijiste</span>
    </div>
  ) : onToggleMastered ? (
    <button
      type="button"
      className={`bf-act know ${w.dom ? 'on' : ''}`}
      aria-pressed={w.dom}
      onClick={() => onToggleMastered(w)}
    >
      {w.dom ? <LibIcKnown /> : <LibIcCheckC size={18} />}
      <span>Ya la sé</span>
    </button>
  ) : w.dom ? (
    <div className="bf-act know on fixed" aria-disabled="true">
      <LibIcKnown />
      <span>Ya la sé</span>
    </div>
  ) : null;
  const save = w.isPhrase ? null : (
    <button
      type="button"
      className={`bf-act save ${w.saved ? 'on' : ''}`}
      aria-pressed={w.saved}
      onClick={() => onToggleSave(w)}
    >
      <LibIcBookmark size={17} fill={w.saved ? 'currentColor' : 'none'} />
      <span>{w.saved ? 'Guardada' : 'Guardar'}</span>
    </button>
  );
  const deck = onAddToDeck ? (
    <button
      type="button"
      className={`bf-act deck ${added ? 'added' : ''}`}
      onClick={() => {
        setAdded(true);
        onAddToDeck(w);
      }}
    >
      {added ? <LibIcCheck size={17} /> : <LibIcCards size={17} />}
      <span>{added ? 'En tu deck' : 'Agregar al deck'}</span>
    </button>
  ) : null;
  // Los botones, en una fila fija al pie (el `footer` de LibSheet, fuera de lo
  // que se desplaza): el estado se ve siempre, aunque la ficha sea larga.
  // Con la tarjeta de una palabra tocada abierta, el pie se apaga e inerte: la
  // tarjeta trae su propio «Guardar» (el de la palabra tocada) y no compite con
  // el de la ficha. Tocar el pie apagado sólo cierra la tarjeta (encargo 70).
  const quiet = tap.active !== null;
  const footer =
    know || save || deck ? (
      <div className={`bf-foot ${quiet ? 'is-quiet' : ''}`} inert={quiet}>
        {know}
        {save}
        {deck}
      </div>
    ) : undefined;

  const mine =
    collection != null || w.fam != null || w.fav || w.hard || w.fromSessions || w.isPhrase;

  return (
    <LibSheet onClose={onClose} footer={footer} variant={presentation}>
      {back && (
        <button type="button" className="bf-back" onClick={back.onBack}>
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
            <LibIcChevR size={11} />
          </span>
          Volver a «{back.label}»
        </button>
      )}
      <div className="bf-head">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 9,
              flexWrap: 'wrap',
              paddingRight: 34,
            }}
          >
            <span className="bf-word">{w.text}</span>
          </div>
          {(ipa || band) && (
            <div className="bf-meta">
              {ipa && <LibAudioIPA word={w.text} ipa={ipa} entry={!w.isPhrase} />}
              {band && (
                <button
                  type="button"
                  className="bf-type"
                  aria-expanded={openExpl === 'band'}
                  onClick={() => toggleExpl('band')}
                >
                  <span className="bf-top">
                    <i style={{ background: band.accent }} />
                    {band.label}
                  </span>
                </button>
              )}
            </div>
          )}
          {band && openExpl === 'band' && (
            <p className="bf-expl" role="note">
              <b>{band.label}</b>: {libBandText(band, w.rank)}.
            </p>
          )}
        </div>
      </div>

      {dictionaryFailed && (
        <p className="bf-dict-err" role="status">
          No pudimos abrir la ficha del diccionario: ves la copia guardada.
        </p>
      )}

      {contextNote && (
        <p className="bf-context-note" role="status">
          {contextNote}
        </p>
      )}

      {meanings && selectedId && (
        <LibMeaningChips
          key={openKey}
          meanings={visibleMeanings!}
          selectedId={selectedId}
          onSelect={(id) => {
            setPick({ of: openKey, id });
            if (openExpl === 'type') setExpl(null);
          }}
        />
      )}
      <LibMeaningPanel
        w={w}
        shown={shown}
        typeOpen={openExpl === 'type'}
        onToggleType={() => toggleExpl('type')}
        tap={tapProps}
        hidden={personallyHidden}
        report={
          reportContext &&
          (reportContext.enabled ||
            reportContext.reports.length > 0 ||
            reportContext.hiddenIds.length > 0) &&
          reportMeaningId ? (
            <MeaningReport
              key={`${w.id}:${reportMeaningId}`}
              word={w.text}
              meaningId={reportMeaningId}
              context={currentMeaningId ? reportContext : { ...reportContext, enabled: false }}
              onContext={onReportContext}
              onRestore={async () => {
                const fresh = await fetchDictionaryEntry(w.text);
                setRestored(
                  fresh.entry
                    ? libOverCollection(libFromLexicon(fresh.entry), initialWord)
                    : initialWord,
                );
              }}
            />
          ) : undefined
        }
      />
      {!personallyHidden && tap.box}

      {/* Bloque contextual (ej. la oración del capítulo en listen) */}
      {usage && !personallyHidden && (
        <div className="bf-block">
          <div className="bf-block-title">{usage.label}</div>
          <div className="bf-ex" style={{ marginTop: 2 }}>
            {usage.node}
          </div>
        </div>
      )}

      {/* Phrasal; la conjugación, sólo si lo que se ve es un verbo («time» abre en «tiempo») */}
      {w.phrasal && !personallyHidden && <LibPhrasalBlock phrasal={w.phrasal} />}
      {w.verbForms && !personallyHidden && shown.pos === LIB_POS_VERB && (
        <LibConjBlock conj={w.verbForms} />
      )}

      {mine && <LibMineBlock w={w} collection={collection} />}
    </LibSheet>
  );
}
